import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  Gift, Check, Loader2, Plus, RefreshCw, Copy, Clock, ShieldCheck, XCircle, Facebook, MessageCircle, Minus, Link2,
  Lock, CreditCard, Smartphone, UserRound, Phone, Building2, Mail, ArrowLeft, Sparkles,
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// Offre des agents IA (Page Facebook / WhatsApp) côté client.
//  1. Le client renseigne ses coordonnées (nom, numéro à contacter, entreprise) puis choisit :
//     - démarrer l'essai gratuit de 7 jours, ou
//     - payer tout de suite : MVola, Orange Money (référence SMS, validé par l'équipe) ou carte bancaire (Stripe, activation automatique)
//  2. À la fin de l'essai l'accès est bloqué jusqu'au paiement : un bandeau « Payer maintenant » s'affiche, et un message est envoyé dans l'app.
// Il faut un emplacement valide (essai ou abonnement) pour connecter une Page / un numéro.
// ============================================================

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' });
const fmtAr = (n) => `${Math.round(n || 0).toLocaleString('fr-FR')} Ar`.replace(/[  ]/g, ' ');
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '');

const THEME = {
  facebook: { from: '#2563eb', to: '#6366f1', icon: Facebook, per: 'par Page et par mois', free: 'Connectez votre Page Facebook' },
  facebook_cm: { from: '#f97316', to: '#ec4899', icon: Sparkles, per: 'par Page et par mois', free: "L'équipe connecte votre Page" },
  whatsapp: { from: '#16a34a', to: '#059669', icon: MessageCircle, per: 'par mois', free: "L'équipe prépare votre agent" },
};
const IS_FB = (c) => c === 'facebook' || c === 'facebook_cm';
const KIND = { trial: 'Essai gratuit', paid: 'Abonnement', gift: 'Offert' };
const STATUS = {
  active: { label: 'Actif', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  expired: { label: 'Terminé', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  stopped: { label: 'Suspendu', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
};

export function useAgentOffer(channel) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/agent-offers/me?channel=${channel}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setMe(data);
    } catch (e) {
      console.error('Erreur chargement offre:', e);
    } finally {
      setLoading(false);
    }
  }, [channel]);
  useEffect(() => { reload(); }, [reload]);
  return { me, loading, reload };
}

const CSS = `
.ao-scene { perspective: 1100px; }
.ao-card { position:relative; border-radius:28px; padding:26px; overflow:hidden; transform-style:preserve-3d; transform:rotateX(3deg); transition:transform .35s;
  background:linear-gradient(155deg,rgba(255,255,255,.11),rgba(255,255,255,.025)); border:1px solid rgba(255,255,255,.18);
  box-shadow:0 40px 80px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.28); }
.ao-card:hover { transform:rotateX(0deg) translateY(-3px); }
.ao-glow { position:absolute; width:300px; height:300px; right:-110px; top:-130px; border-radius:50%; filter:blur(60px); opacity:.35; pointer-events:none; }
.ao-amount { font-family:'Space Grotesk','DM Sans',sans-serif; font-weight:800; line-height:1; color:#fff; font-size:clamp(40px,9vw,64px);
  text-shadow:1px 1px 0 #3b2fb0,2px 2px 0 #3b2fb0,3px 3px 0 #342aa0,4px 4px 0 #342aa0,5px 5px 0 #2d2590,6px 6px 0 #2d2590,10px 14px 24px rgba(0,0,0,.55); }
.ao-btn3d { display:inline-flex; align-items:center; justify-content:center; gap:9px; padding:13px 22px; border-radius:16px; font-weight:800; font-size:15px; color:#fff; border:0; cursor:pointer;
  transition:transform .15s, box-shadow .15s; }
.ao-btn3d:active { transform:translateY(3px); }
.ao-btn3d:disabled { opacity:.55; cursor:not-allowed; }
.ao-trial { background:linear-gradient(135deg,#86efac,#4ade80); color:#052e1a; box-shadow:0 6px 0 #15803d, 0 18px 30px rgba(74,222,128,.35); }
.ao-trial:active { box-shadow:0 3px 0 #15803d, 0 10px 20px rgba(74,222,128,.3); }
.ao-pay { background:linear-gradient(135deg,var(--a),var(--b)); box-shadow:0 6px 0 rgba(0,0,0,.35), 0 18px 30px rgba(0,0,0,.35); }
.ao-pay:active { box-shadow:0 3px 0 rgba(0,0,0,.35), 0 10px 20px rgba(0,0,0,.3); }
.ao-choice { position:relative; border-radius:22px; padding:18px; text-align:left; transition:transform .25s, box-shadow .25s; transform:perspective(900px) rotateX(2deg);
  background:linear-gradient(155deg,rgba(255,255,255,.09),rgba(255,255,255,.02)); border:1px solid rgba(255,255,255,.16); box-shadow:0 22px 44px rgba(0,0,0,.38); }
.ao-choice:hover { transform:perspective(900px) rotateX(0) translateY(-3px); }
@media (prefers-reduced-motion: reduce) { .ao-card, .ao-btn3d, .ao-choice { transition:none; } }
`;

function Styles() { return <style>{CSS}</style>; }

const digits = (v) => (v || '').replace(/\D/g, '');

// ------------------------------------------------------------ paiement par carte (Stripe)
function CardForm({ orderId, total, onDone, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setError('');
    try {
      const { error: err } = await stripe.confirmPayment({ elements, confirmParams: { return_url: `${window.location.origin}/payment/success` }, redirect: 'if_required' });
      if (err) { setError(err.message || 'Paiement refusé.'); setBusy(false); return; }
      // paiement réussi : on active tout de suite (le serveur vérifie auprès de Stripe)
      await fetch(`${API_URL}/api/agent-offers/stripe-confirm`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ order_id: orderId }) });
      onDone();
    } catch (_) { setError('Une erreur est survenue. Réessayez.'); setBusy(false); }
  };
  return (
    <form onSubmit={pay} className="space-y-4">
      <div className="rounded-xl overflow-hidden"><PaymentElement /></div>
      {error && <p className="flex items-center gap-2 text-sm text-rose-300"><XCircle className="w-4 h-4 shrink-0" /> {error}</p>}
      <div className="flex gap-3">
        <button type="button" onClick={onBack} disabled={busy} className="px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm disabled:opacity-50"><ArrowLeft className="w-4 h-4" /></button>
        <button type="submit" disabled={!stripe || busy} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-4 h-4" />} Payer {fmtAr(total)}
        </button>
      </div>
      <p className="text-[11px] text-gray-500 text-center flex items-center justify-center gap-1.5"><Lock className="w-3 h-3" /> Paiement 100 % sécurisé · Stripe</p>
    </form>
  );
}

function CardPayment({ intent, onDone, onBack }) {
  const [stripePromise, setStripePromise] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/stripe/config`);
        const d = await r.json();
        setStripePromise(loadStripe(d.publishable_key));
      } catch (_) { setError('Impossible de charger le paiement par carte.'); }
    })();
  }, []);
  if (error) return <p className="text-sm text-rose-300">{error}</p>;
  if (!stripePromise) return <div className="py-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>;
  const appearance = { theme: 'night', variables: { colorPrimary: '#a855f7', colorBackground: '#1e1b4b', colorText: '#ffffff', colorDanger: '#ef4444', borderRadius: '12px' } };
  return (
    <Elements stripe={stripePromise} options={{ clientSecret: intent.client_secret, appearance }}>
      <CardForm orderId={intent.order_id} total={intent.amount} onDone={onDone} onBack={onBack} />
    </Elements>
  );
}

// ------------------------------------------------------------ formulaire de paiement (MVola / Orange Money / carte)
function OrderForm({ me, channel, target, contact, onClose, onDone }) {
  const t = THEME[channel];
  const [qty, setQty] = useState(1);
  const [months, setMonths] = useState(1);
  const [method, setMethod] = useState('mvola');
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [intent, setIntent] = useState(null);
  // renouvellement / ajout depuis « Mon offre » : on réutilise les coordonnées connues ; sinon on les demande ici
  const askContact = !contact && !me.contact?.known;
  const [lc, setLc] = useState({ full_name: me.contact?.full_name || '', phone: '' });
  const effContact = contact || (askContact ? lc : undefined);
  const contactOk = !askContact || (lc.full_name.trim().length >= 2 && digits(lc.phone).length >= 9);
  const total = me.price_ar * (target ? 1 : qty) * months;
  const pay = me.payment?.[method];
  const methods = [...Object.entries(me.payment || {}).map(([k, v]) => ({ key: k, label: v.label, icon: Smartphone })),
    ...(me.stripe_enabled ? [{ key: 'card', label: 'Carte bancaire', icon: CreditCard }] : [])];
  const body = (extra) => JSON.stringify({ channel, quantity: target ? 1 : qty, months, target_sub_id: target || '', contact: effContact, ...extra });

  const copy = async () => {
    try { await navigator.clipboard.writeText((pay?.number || '').replace(/\s/g, '')); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { /* presse-papiers indisponible */ }
  };
  const submitManual = async (e) => {
    e.preventDefault();
    if (!contactOk) { setError('Indiquez votre nom et le numéro de téléphone à contacter.'); return; }
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/agent-offers/orders`, { method: 'POST', headers: authHeaders(), body: body({ method, payment_ref: ref }) });
      const data = await res.json();
      if (data.success) onDone(); else setError(data.message || 'Paiement non enregistré.');
    } catch (_) { setError('Connexion impossible. Réessayez.'); } finally { setBusy(false); }
  };
  const startCard = async () => {
    if (!contactOk) { setError('Indiquez votre nom et le numéro de téléphone à contacter.'); return; }
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/agent-offers/stripe-intent`, { method: 'POST', headers: authHeaders(), body: body({}) });
      const data = await res.json();
      if (data.success) setIntent(data); else setError(data.message || 'Paiement par carte indisponible.');
    } catch (_) { setError('Connexion impossible. Réessayez.'); } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-2xl border border-white/15 bg-black/25 p-4 md:p-5 space-y-4 text-left">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-white font-bold">{target ? 'Renouveler mon abonnement' : `Payer mon abonnement ${channel === 'facebook_cm' ? 'Messenger + CM Studio' : channel === 'facebook' ? 'Messenger Bot' : 'WhatsApp'}`}</h4>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-white text-sm">Annuler</button>
      </div>

      {intent ? (
        <>
          <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-sm text-gray-200 space-y-1">
            <div className="flex justify-between"><span>Abonnement</span><span>{fmtAr(intent.base_amount)}</span></div>
            <div className="flex justify-between text-gray-400"><span>Frais carte bancaire (2,9 % + frais fixe)</span><span>+{fmtAr(intent.stripe_fee_ar)}</span></div>
            <div className="flex justify-between font-bold text-white pt-1 border-t border-white/10"><span>Total</span><span>{fmtAr(intent.amount)}</span></div>
          </div>
          <CardPayment intent={intent} onDone={onDone} onBack={() => setIntent(null)} />
        </>
      ) : (
        <>
          {!target && IS_FB(channel) && (
            <div className="flex items-center justify-between gap-3 rounded-xl bg-white/5 border border-white/10 px-3 py-2">
              <span className="text-sm text-gray-200">Combien de Pages ?</span>
              <span className="flex items-center gap-2">
                <button type="button" onClick={() => setQty(q => Math.max(1, q - 1))} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                <b className="w-6 text-center text-white">{qty}</b>
                <button type="button" onClick={() => setQty(q => Math.min(10, q + 1))} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </span>
            </div>
          )}

          <div>
            <div className="text-xs text-gray-400 mb-1.5">Durée</div>
            <div className="flex flex-wrap gap-2">
              {[1, 3, 6, 12].map(m => (
                <button type="button" key={m} onClick={() => setMonths(m)}
                  className={`px-4 py-1.5 rounded-full text-sm border transition ${months === m ? 'bg-white/20 border-white/50 text-white font-semibold' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>
                  {m} mois
                </button>
              ))}
            </div>
          </div>

          {askContact && (
            <div className="grid gap-2 sm:grid-cols-2">
              <input value={lc.full_name} onChange={e => setLc(v => ({ ...v, full_name: e.target.value }))} placeholder="Votre nom complet"
                className="bg-black/30 border border-white/15 focus:border-white/50 rounded-xl px-4 py-2.5 text-white text-sm outline-none" />
              <input value={lc.phone} onChange={e => setLc(v => ({ ...v, phone: e.target.value }))} inputMode="tel" placeholder="Numéro à contacter"
                className="bg-black/30 border border-white/15 focus:border-white/50 rounded-xl px-4 py-2.5 text-white text-sm outline-none" />
            </div>
          )}

          <div className="flex items-end justify-between rounded-xl bg-white/5 border border-white/10 px-4 py-3">
            <span className="text-sm text-gray-300">Total à payer</span>
            <b className="text-2xl text-white tabular-nums">{fmtAr(total)}</b>
          </div>

          <div>
            <div className="text-xs text-gray-400 mb-1.5">Payer par</div>
            <div className={`grid gap-2 ${methods.length > 2 ? 'grid-cols-3' : 'grid-cols-2'}`}>
              {methods.map(m => (
                <button type="button" key={m.key} onClick={() => setMethod(m.key)}
                  className={`rounded-xl px-2 py-2.5 text-sm border transition flex flex-col items-center gap-1 ${method === m.key ? 'bg-white/15 border-white/50 text-white font-semibold' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>
                  <m.icon className="w-4 h-4" /> <span className="text-xs sm:text-sm">{m.label}</span>
                </button>
              ))}
            </div>
          </div>

          {method === 'card' ? (
            <>
              <p className="text-xs text-gray-400">Paiement par carte : des frais bancaires (2,9 % + frais fixe) s'ajoutent au prix. Votre offre s'active <b className="text-gray-200">tout de suite</b>, sans attendre de validation.</p>
              {error && <p className="flex items-center gap-2 text-sm text-rose-300"><XCircle className="w-4 h-4 shrink-0" /> {error}</p>}
              <button type="button" onClick={startCard} disabled={busy} className="ao-btn3d ao-pay w-full" style={{ '--a': t.from, '--b': t.to }}>
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />} Continuer vers le paiement par carte
              </button>
            </>
          ) : (
            <form onSubmit={submitManual} className="space-y-4">
              {pay && (
                <div className="rounded-xl bg-sky-500/10 border border-sky-400/25 p-3 text-sm text-sky-100 flex items-center justify-between gap-3">
                  <span>Envoyez <b>{fmtAr(total)}</b> au <b className="tabular-nums">{pay.number}</b><span className="block text-xs text-sky-200/70">au nom de {pay.name}</span></span>
                  <button type="button" onClick={copy} className="shrink-0 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs flex items-center gap-1.5">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copié' : 'Copier'}
                  </button>
                </div>
              )}
              <div>
                <label className="text-xs text-gray-400 mb-1.5 block">Référence de la transaction (reçue par SMS)</label>
                <input value={ref} onChange={e => setRef(e.target.value)} placeholder="Ex : 2410170123456" required minLength={4}
                  className="w-full bg-black/30 border border-white/15 focus:border-white/50 rounded-xl px-4 py-2.5 text-white text-sm outline-none" />
              </div>
              {error && <p className="flex items-center gap-2 text-sm text-rose-300"><XCircle className="w-4 h-4 shrink-0" /> {error}</p>}
              <button type="submit" disabled={busy} className="ao-btn3d ao-pay w-full" style={{ '--a': t.from, '--b': t.to }}>
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />} J'ai payé — envoyer pour validation
              </button>
              <p className="text-[11px] text-gray-500 text-center">Notre équipe vérifie le paiement puis active votre offre. Vous êtes prévenu dans votre boîte de réception.</p>
            </form>
          )}
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------ paiement en attente
function PendingCard({ order }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4 text-left">
      <Clock className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
      <div className="text-sm text-amber-100">
        <b>Paiement en cours de vérification</b> — {fmtAr(order.amount_ar)} (réf. {order.payment_ref}).
        <span className="block text-amber-200/80 text-xs mt-0.5">Notre équipe valide votre paiement très vite : vous recevrez un message dans votre boîte de réception.</span>
      </div>
    </div>
  );
}

function Field({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2.5 bg-black/25 border border-white/12 focus-within:border-white/45 rounded-xl px-3.5 py-2.5 transition-colors">
      <Icon className="w-4 h-4 text-gray-400 shrink-0" />
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

// ------------------------------------------------------------ composant principal
// show = 'gate'  : coordonnées + choix (essai gratuit / payer maintenant) quand il n'y a pas d'accès
// show = 'panel' : « Mon offre » (emplacements, jours restants, renouveler / ajouter) + bandeau de blocage si l'offre est terminée
export default function AgentOffer({ channel, me, reload, show = 'gate', onSupport }) {
  const t = THEME[channel];
  const Icon = t.icon;
  const [asking, setAsking] = useState(false);
  const [asked, setAsked] = useState(false);
  // question sur l'offre WhatsApp : un message est posté pour le client dans le Support (l'équipe est alertée sur Telegram)
  const askSupport = async () => {
    setAsking(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/agent-offers/cm-interest`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ topic: 'whatsapp' }) });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'Envoi impossible.');
      setAsked(true);
      if (onSupport) setTimeout(onSupport, 500);
    } catch (e) { setError(e.message || 'Envoi impossible. Réessayez.'); } finally { setAsking(false); }
  };
  const [ordering, setOrdering] = useState(null);   // { target }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ full_name: '', phone: '', business_name: '', message: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // préremplit avec ce que le serveur connaît déjà (profil, dernier contact)
  useEffect(() => {
    if (!me?.contact) return;
    setForm(f => ({
      full_name: f.full_name || me.contact.full_name || '', phone: f.phone || me.contact.phone || '',
      business_name: f.business_name || me.contact.business_name || '', message: f.message,
    }));
  }, [me?.contact?.full_name, me?.contact?.phone, me?.contact?.business_name]); // eslint-disable-line react-hooks/exhaustive-deps

  const formOk = () => form.full_name.trim().length >= 2 && digits(form.phone).length >= 9;
  const needForm = () => { setError('Indiquez votre nom et le numéro de téléphone à contacter (9 chiffres minimum).'); };

  const startTrial = async () => {
    if (!formOk()) { needForm(); return; }
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/agent-offers/trial`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ channel, ...form }) });
      const data = await res.json();
      if (data.success) await reload(); else setError(data.message || "Impossible d'activer l'essai.");
    } catch (_) { setError('Connexion impossible. Réessayez.'); } finally { setBusy(false); }
  };
  const startPay = (target) => {
    if (show === 'gate' && !formOk()) { needForm(); return; }
    setError(''); setOrdering({ target });
  };
  const done = async () => { setOrdering(null); await reload(); };

  if (!me) return null;
  const contact = show === 'gate' ? form : undefined;

  if (show === 'gate') {
    return (
      <div className="ao-scene mb-8">
        <Styles />
        <div className="ao-card">
          <div className="ao-glow" style={{ background: t.from }} />
          <div className="relative">
            <div className="text-center">
              <div className="w-16 h-16 rounded-3xl mx-auto mb-4 flex items-center justify-center text-white shadow-2xl" style={{ background: `linear-gradient(145deg,${t.from},${t.to})` }}>
                <Icon className="w-8 h-8" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-white">{channel === 'facebook_cm' ? 'Activez Messenger Bot + Community Manager Studio' : channel === 'facebook' ? 'Activez votre agent IA Messenger' : 'Activez votre agent IA WhatsApp'}</h2>
              <div className="mt-5 flex flex-wrap items-end justify-center gap-x-4 gap-y-1">
                <span className="ao-amount">{fmtAr(me.price_ar)}</span>
                <span className="text-blue-100 font-bold pb-1.5">{t.per}</span>
              </div>
              <p className="text-sm text-blue-100/80 mt-3">
                {channel === 'facebook_cm' ? 'Messenger illimité + Community Manager Studio · mise en place par notre équipe' : channel === 'facebook' ? 'Messenger illimité · plusieurs Pages possibles : un abonnement par Page' : `Inclus : ${(me.limits?.messages || 2000).toLocaleString('fr-FR')} messages et ${me.limits?.contacts || 250} clients différents par mois · configuration par notre équipe`}
              </p>
            </div>

            {me.pending_order ? (
              <div className="mt-6"><PendingCard order={me.pending_order} /></div>
            ) : (
              <>
                {/* 1. coordonnées */}
                <div className="mt-7">
                  <div className="text-xs font-bold tracking-widest uppercase text-white/55 mb-2.5">1 · Vos informations</div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <Field icon={UserRound}><input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Votre nom complet" className="w-full bg-transparent text-white text-sm placeholder-gray-500 outline-none" /></Field>
                    <Field icon={Mail}><input value={me.contact?.email || ''} readOnly placeholder="Votre email" className="w-full bg-transparent text-gray-300 text-sm outline-none cursor-default" /></Field>
                    <Field icon={Phone}><input value={form.phone} onChange={e => set('phone', e.target.value)} inputMode="tel" placeholder="Numéro à contacter (WhatsApp de préférence)" className="w-full bg-transparent text-white text-sm placeholder-gray-500 outline-none" /></Field>
                    <Field icon={Building2}><input value={form.business_name} onChange={e => set('business_name', e.target.value)} placeholder={IS_FB(channel) ? 'Nom de votre Page / entreprise' : 'Nom de votre entreprise'} className="w-full bg-transparent text-white text-sm placeholder-gray-500 outline-none" /></Field>
                  </div>
                  <textarea value={form.message} onChange={e => set('message', e.target.value)} rows={2} placeholder="Parlez-nous de votre activité (facultatif) : ce que vous vendez, ce que l'agent doit faire…"
                    className="mt-3 w-full bg-black/25 border border-white/12 focus:border-white/45 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 outline-none resize-none" />
                </div>

                {/* 2. choix */}
                <div className="mt-6">
                  <div className="text-xs font-bold tracking-widest uppercase text-white/55 mb-2.5">{channel !== 'facebook' || !me.trial_available ? '2 · Réglez votre abonnement' : '2 · Choisissez comment démarrer'}</div>
                  <div className={`grid gap-4 ${channel !== 'facebook' || !me.trial_available ? '' : 'md:grid-cols-2'}`}>
                    {channel === 'facebook' && me.trial_available && (
                    <div className="ao-choice">
                      <div className="flex items-center gap-2 text-white font-bold"><Gift className="w-5 h-5 text-emerald-300" /> {me.trial_days} jours gratuits</div>
                      <p className="text-sm text-gray-300 mt-1.5">Testez l'agent sans rien payer. À la fin, l'accès est bloqué jusqu'au paiement.</p>
                      {me.trial_available ? (
                        <button onClick={startTrial} disabled={busy} className="ao-btn3d ao-trial w-full mt-4">
                          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Gift className="w-5 h-5" />} Commencer l'essai gratuit
                        </button>
                      ) : <p className="mt-4 text-xs text-gray-400">{me.trial_blocked_reason || 'Votre essai gratuit a déjà été utilisé.'}</p>}
                    </div>
                    )}
                    <div className="ao-choice">
                      <div className="flex items-center gap-2 text-white font-bold"><ShieldCheck className="w-5 h-5 text-sky-300" /> Payer maintenant</div>
                      <p className="text-sm text-gray-300 mt-1.5">MVola, Orange Money ou carte bancaire. Avec la carte, l'offre s'active tout de suite.</p>
                      <div className={channel === 'whatsapp' ? 'mt-4 grid gap-3 sm:grid-cols-2' : ''}>
                      <button onClick={() => startPay('')} className={`ao-btn3d ao-pay w-full ${channel === 'whatsapp' ? '' : 'mt-4'}`} style={{ '--a': t.from, '--b': t.to }}>
                        <ShieldCheck className="w-5 h-5" /> Payer l'abonnement
                      </button>
                      {channel === 'whatsapp' && (
                        <button onClick={askSupport} disabled={asking || asked} className="ao-btn3d w-full" style={{ background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.25)', boxShadow: '0 6px 0 rgba(0,0,0,.28)' }}>
                          {asking ? <Loader2 className="w-5 h-5 animate-spin" /> : asked ? <Check className="w-5 h-5 text-emerald-300" /> : <MessageCircle className="w-5 h-5" />}
                          {asked ? 'Message envoyé à l\'équipe' : 'Contacter le support'}
                        </button>
                      )}
                      </div>
                    </div>
                  </div>
                  {error && <p className="mt-3 text-sm text-rose-300 flex items-center gap-2"><XCircle className="w-4 h-4 shrink-0" /> {error}</p>}
                  {ordering && <OrderForm me={me} channel={channel} target={ordering.target} contact={contact} onClose={() => setOrdering(null)} onDone={done} />}
                </div>
              </>
            )}
            <ul className="mt-6 grid gap-2 text-left max-w-md mx-auto text-sm text-gray-200">
              {['Sans engagement : vous payez mois par mois', 'Réponses 24h/24 en français et en malgache', 'Configuration et suivi par notre équipe'].map(x => (
                <li key={x} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {x}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // ----- panel « Mon offre »
  if (!me.slots.length && !me.pending_order) return null;
  const anyActive = me.slots.some(s => s.status === 'active');
  const blocked = !anyActive && me.slots.find(s => s.status === 'expired');
  const stoppedOnly = !anyActive && !blocked && me.slots.find(s => s.status === 'stopped');
  return (
    <div className="mb-8">
      <Styles />

      {/* accès bloqué : essai / abonnement terminé */}
      {blocked && !me.pending_order && (
        <div className="mb-4 rounded-2xl border border-rose-400/40 bg-gradient-to-br from-rose-500/20 to-orange-500/10 p-4 md:p-5" style={{ boxShadow: '0 20px 40px rgba(244,63,94,.18)' }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/25 flex items-center justify-center shrink-0"><Lock className="w-5 h-5 text-rose-200" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold">{blocked.kind === 'trial' ? 'Votre essai gratuit est terminé' : 'Votre abonnement est terminé'} — accès bloqué</div>
              <p className="text-sm text-rose-100/85 mt-1">Votre agent IA est arrêté. Payez l'abonnement ({fmtAr(me.price_ar)} {t.per}) pour le relancer : MVola, Orange Money ou carte bancaire.</p>
              {!ordering && (
                <button onClick={() => startPay(blocked.id)} className="ao-btn3d ao-pay mt-3" style={{ '--a': '#f43f5e', '--b': '#f97316' }}>
                  <ShieldCheck className="w-5 h-5" /> Payer maintenant
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {stoppedOnly && (
        <div className="mb-4 rounded-2xl border border-rose-400/40 bg-rose-500/10 p-4 text-sm text-rose-100">
          <b>Offre suspendue par l'équipe</b>{stoppedOnly.stop_reason ? ` — ${stoppedOnly.stop_reason}` : ''}. Contactez-nous pour la réactiver.
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-white font-bold text-lg flex items-center gap-2"><Icon className="w-5 h-5" style={{ color: t.from }} /> Mon offre</h3>
        {!me.pending_order && !ordering && (
          <button onClick={() => startPay('')} className="px-3.5 py-2 rounded-xl text-xs font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/15 flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Ajouter {IS_FB(channel) ? 'une Page' : 'un numéro'} ({fmtAr(me.price_ar)}/mois)
          </button>
        )}
      </div>
      {me.pending_order && <div className="mb-3"><PendingCard order={me.pending_order} /></div>}
      <div className="grid gap-3 md:grid-cols-2">
        {me.slots.map(s => {
          const st = STATUS[s.status] || STATUS.expired;
          const total = s.kind === 'trial' ? 7 : 30;
          const pct = s.status === 'active' ? Math.max(4, Math.min(100, ((s.days_left || 0) / total) * 100)) : 0;
          return (
            <div key={s.id} className="rounded-2xl border border-white/12 bg-white/[0.04] p-4" style={{ boxShadow: '0 14px 30px rgba(0,0,0,.3)' }}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-white font-semibold text-sm flex items-center gap-2">
                  {s.kind === 'trial' ? <Gift className="w-4 h-4 text-emerald-300" /> : <ShieldCheck className="w-4 h-4 text-sky-300" />} {KIND[s.kind] || s.kind}{s.channel === 'facebook_cm' ? ' · Messenger + CM Studio' : (s.channel === 'facebook' ? ' · Messenger Bot' : '')}
                </span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
              </div>
              <div className="mt-2 text-xs text-gray-300 flex items-center gap-1.5">
                <Link2 className="w-3.5 h-3.5 text-gray-400" />
                {s.page_name ? <span className="truncate">{s.page_name}</span> : <span className="text-gray-400">{s.status === 'active' ? `Libre — ${t.free.toLowerCase()}` : 'Non utilisé'}</span>}
              </div>
              {s.status === 'active' && (
                <>
                  <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: `linear-gradient(90deg,${t.from},${t.to})` }} /></div>
                  <div className="mt-1.5 text-xs text-gray-300">Reste <b className="text-white">{s.days_left} jour{s.days_left > 1 ? 's' : ''}</b> · fin le {fmtDay(s.expires_at)}</div>
                </>
              )}
              {s.status === 'expired' && <div className="mt-2 text-xs text-amber-200/90">Terminé le {fmtDay(s.expires_at)} — l'agent IA est arrêté. Renouvelez pour le relancer.</div>}
              {s.status === 'stopped' && <div className="mt-2 text-xs text-rose-200/90">Suspendu par l'équipe{s.stop_reason ? ` : ${s.stop_reason}` : ''}. Contactez-nous.</div>}
              {s.status !== 'stopped' && !me.pending_order && (
                <button onClick={() => startPay(s.id)}
                  className="mt-3 w-full px-3 py-2 rounded-xl text-xs font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/15 flex items-center justify-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" /> {s.kind === 'trial' ? "Passer à l'abonnement" : 'Renouveler'}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {ordering && <OrderForm me={me} channel={channel} target={ordering.target} contact={undefined} onClose={() => setOrdering(null)} onDone={done} />}
      {error && <p className="mt-2 text-sm text-rose-300">{error}</p>}
    </div>
  );
}
